import { useCallback, useMemo, useState } from "react";
import cx from "classnames";

import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import Input from "ds/components/Input";
import Typography from "ds/components/Typography";
import TreeListMultiSelect from "ds/components/TreeList/MultiSelect";
import { Magnify } from "components/icons/generated";

import { useSpaceSelection } from "./useSpaceSelection";
import SpaceSelectorDebugger from "./Debugger";
import SpaceSelectorAvailabilitySummary from "./AvailabilitySummary";
import styles from "./styles.module.css";

type SpaceData = {
  id: string;
  name: string;
  parentSpace: string | null;
  inheritEntities: boolean;
};

type SpaceSelectorProps = {
  spaces: SpaceData[];
  selectedSpaceIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  disabledSpaceIds?: string[];
  hiddenSpaceIds?: string[];
  showAvailabilitySummary?: boolean;
  defaultSelection?: string;
  debugging?: boolean;
  showSearch?: boolean;
  searchPhrase?: string;
  tableLayout?: boolean;
};

const SpaceSelector = ({
  spaces,
  selectedSpaceIds = new Set(),
  onSelectionChange,
  disabledSpaceIds = [],
  hiddenSpaceIds,
  showAvailabilitySummary = true,
  defaultSelection,
  debugging = false,
  showSearch = true,
  searchPhrase,
  tableLayout,
}: SpaceSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  // Merge defaultSelection into selectedSpaceIds so the inheritance logic applies
  const effectiveSelectedSpaceIds = useMemo(() => {
    const merged = new Set(selectedSpaceIds);
    if (defaultSelection) {
      merged.add(defaultSelection);
    }
    return merged;
  }, [selectedSpaceIds, defaultSelection]);

  // Wrap onSelectionChange to filter out the defaultSelection from the result
  const handleSelectionChangeWrapper = useCallback(
    (newSelectedIds: Set<string>) => {
      if (!onSelectionChange) return;

      // Remove defaultSelection from the result before calling the parent handler
      const filtered = new Set(newSelectedIds);
      if (defaultSelection) {
        filtered.delete(defaultSelection);
      }
      onSelectionChange(filtered);
    },
    [onSelectionChange, defaultSelection]
  );

  const {
    checkedSpaceIds,
    disabledSpaceIds: autoDisabledSpaceIds,
    handleSelectionChange,
  } = useSpaceSelection({
    spaces,
    selectedSpaceIds: effectiveSelectedSpaceIds,
    onSelectionChange: handleSelectionChangeWrapper,
    defaultSelection,
  });

  // Merge user-provided disabled IDs with auto-disabled IDs
  const allDisabledSpaceIds = useMemo(
    () => [...disabledSpaceIds, ...autoDisabledSpaceIds],
    [disabledSpaceIds, autoDisabledSpaceIds]
  );

  return (
    <Box direction="column" gap="xx-large" className={styles.container}>
      {debugging && (
        <SpaceSelectorDebugger spaces={spaces} selectedSpacesIds={effectiveSelectedSpaceIds} />
      )}

      {showSearch && (
        <Box className={styles.searchInputWrapper}>
          <Input
            type="text"
            placeholder={SEARCH_PLACEHOLDERS.SPACES}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
          <Icon className={styles.searchIcon} src={Magnify} />
        </Box>
      )}

      <Box direction="column" gap="medium">
        {showAvailabilitySummary && (
          <SpaceSelectorAvailabilitySummary spaces={spaces} checkedSpaceIds={checkedSpaceIds} />
        )}

        <Box
          className={cx(styles.treeWrapper, tableLayout && styles.tableLayout)}
          direction="column"
        >
          <Box justify="between" className={styles.treeHeader}>
            <Box padding="x-large">
              <Typography variant="p-t7" tag="div" color="secondary">
                Name
              </Typography>
            </Box>
            <Box padding="x-large">
              <Typography variant="p-t7" tag="div" color="secondary">
                Inheritance
              </Typography>
            </Box>
          </Box>
          <Box padding="large large 0 large" className={styles.treeListWrapper}>
            <TreeListMultiSelect
              data={spaces}
              idKey="id"
              parentKey="parentSpace"
              nameKey="name"
              selectedKeys={checkedSpaceIds}
              onSelectionChange={handleSelectionChange}
              searchQuery={!showSearch && searchPhrase ? searchPhrase : searchQuery}
              disabledKeys={allDisabledSpaceIds}
              hiddenKeys={hiddenSpaceIds}
              ariaLabel="Select spaces"
              disableIndeterminate
              defaultSelection={defaultSelection}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

SpaceSelector.displayName = "SpaceSelector";

export default SpaceSelector;
